import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { initFirebaseSDK, rtdb } from "../firebase/firebaseClient";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState({ online: true, latency: 28 });

  // Periodically check RTDB ping
  useEffect(() => {
    let isMounted = true;
    const checkPing = async () => {
      try {
        if (typeof rtdb?.ping === "function") {
          const res = await rtdb.ping();
          if (isMounted && res) {
            setConnectionStatus(res);
          }
        }
      } catch (err) {
        // Silently handled to keep production console clean (term.md Pasal 7)
      }
    };
    checkPing();
    const timer = setInterval(checkPing, 20000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  // Check and verify admin privileges purely against the server (Zero frontend leaks)
  const verifyAdminPrivileges = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return false;
    try {
      const testAdminAccess = await rtdb.get("system/admin_key");
      return testAdminAccess !== null && testAdminAccess !== undefined;
    } catch (e) {
      return false;
    }
  }, []);

  // Listen to Firebase Auth state
  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    async function initAuthListener() {
      try {
        const sdk = await initFirebaseSDK();
        unsubscribe = sdk.onAuthStateChanged(sdk.auth, async (fbUser) => {
          if (!isMounted) return;
          if (fbUser) {
            const isAdmin = await verifyAdminPrivileges(fbUser);
            if (isAdmin) {
              setUser({
                uid: fbUser.uid,
                email: fbUser.email,
                displayName: fbUser.displayName || "Admin Operator",
                photoURL: fbUser.photoURL || "https://databaseimgberry.edgeone.app/berry_113.png",
                role: "admin",
                isAdmin: true
              });
              setAuthError("");
            } else {
              setUser(null);
              setAuthError(`Access Denied: Google Account "${fbUser.email}" is not authorized as Administrator.`);
            }
          } else {
            setUser(null);
          }
          setLoading(false);
        });
      } catch (err) {
        console.warn("Failed to initialize Firebase Auth listener:", err);
        setLoading(false);
      }
    }

    initAuthListener();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [verifyAdminPrivileges]);

  // Strict Google Login popup
  const loginWithGoogle = useCallback(async () => {
    setLoading(true);
    setAuthError("");
    try {
      const sdk = await initFirebaseSDK();
      const result = await sdk.signInWithPopup(sdk.auth, sdk.googleProvider);
      const fbUser = result.user;

      const isAdmin = await verifyAdminPrivileges(fbUser);
      if (!isAdmin) {
        await sdk.signOut(sdk.auth);
        setUser(null);
        const errMsg = `Access Denied: Google Account "${fbUser.email}" is not authorized in Security Rules.`;
        setAuthError(errMsg);
        throw new Error(errMsg);
      }

      const adminUser = {
        uid: fbUser.uid,
        email: fbUser.email,
        displayName: fbUser.displayName || "Admin Operator",
        photoURL: fbUser.photoURL || "https://databaseimgberry.edgeone.app/berry_113.png",
        role: "admin",
        isAdmin: true
      };

      setUser(adminUser);
      setAuthError("");
      return adminUser;
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") {
        setAuthError("Google Sign-In popup was closed before completion.");
      } else {
        setAuthError(err.message || "Failed to authenticate via Google.");
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, [verifyAdminPrivileges]);

  // Logout
  const logout = useCallback(async () => {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) await sdk.signOut(sdk.auth);
    } catch (e) {
      console.warn("SignOut warning:", e);
    } finally {
      setUser(null);
      setAuthError("");
    }
  }, []);

  const value = {
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    isAdmin: !!user?.isAdmin,
    connectionStatus,
    loginWithGoogle,
    logout
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
}
